import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PaginationLabelsProvider } from '@attendance/ui/components/Pagination';

/* ===========================================================================
 * THE PAGER'S WORDS, ONCE, FOR THE WHOLE APP.
 *
 * `@attendance/ui`'s `Pagination` takes its labels from a context now rather
 * than hard-coding English (see the note in Pagination.tsx). This is the one
 * place that fills it, so a screen rendering a pager writes nothing: the
 * alternative was the same six `t()` lookups at nineteen call sites, and a
 * screen tired of copying them is exactly how this app grew a private pager
 * (`AttendancePager`, now deleted) — the second control language §6 of
 * docs/design-rules.md exists to prevent.
 *
 * Mounted at the entry, above the router, because a pager can appear on any
 * route. A `Pagination` rendered with no provider above it — a unit test, the
 * installer — keeps the primitive's English default, so nothing breaks by
 * being rendered in isolation.
 *
 * WHY THE RANGE SENTENCE CARRIES NO NOUN. The English default says "… of 57
 * devices", pluralised by appending an "s". That noun cannot survive
 * translation as a parameter: Arabic has six plural forms and the counted
 * noun's own ending changes with the figure, so a key per caller ("device",
 * "person", "movement", "location", …) would be the only correct spelling of
 * it — eight more key sets to keep in step for a word the table's caption and
 * heading already say. The pager states HOW MANY, which is the fact it owns.
 * ======================================================================== */
export function PagerLabels({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  return (
    <PaginationLabelsProvider
      labels={{
        range: ({ from, to, total }) =>
          t('pager.showing', 'Showing {{from}}–{{to}} of {{total}}', { from, to, total }),
        page: ({ page, pageCount }) =>
          t('pager.page', 'Page {{page}} of {{pageCount}}', { page, pageCount }),
        previousAria: t('pager.previousLabel', 'Previous page'),
        nextAria: t('pager.nextLabel', 'Next page'),
      }}
    >
      {children}
    </PaginationLabelsProvider>
  );
}
